/**
 * The show/hide "eye" inside every password field.
 *
 * A password box that cannot be read back is the single most common cause of a failed
 * sign-in that the user cannot explain, and both handoffs draw the reveal control at the
 * right edge of the field. Serenity's `PasswordEditor` is nothing but
 * `<input type="password">` (its whole class body is `createDefaultElement`), so the affordance
 * has to be added here.
 *
 * WHY A DOM WATCHER AND NOT AN EDITOR SUBCLASS
 * Password inputs are built by `PropertyGrid` from `[PasswordEditor]` in a form — sign-in,
 * sign-up, the user dialog, Serenity.Extensions' own change/reset password screens, the editor
 * gallery. There is no post-attach hook on the widget (the button must be a SIBLING of the
 * input, which only exists once the grid has placed it), and subclassing would only cover the
 * forms this application happens to declare. Watching the document covers every one of them,
 * including screens that ship inside a package — the same reasoning as StackedDialogs.ts.
 *
 * ⚠ THE BUTTON MUST BE `type="button"`. On the sign-in screen the field sits inside a real
 * `<form>`, and a bare `<button>` defaults to `type="submit"` — clicking the eye would post the
 * form instead of revealing anything.
 *
 * ⚠ THE INPUT IS MARKED WITH A CLASS, NOT MATCHED ON `[type="password"]`, and that is the point
 * of `PWD_CLASS`. Revealing works by rewriting `input.type` to "text", so a stylesheet rule
 * written against `input[type="password"]` STOPS MATCHING the moment the eye is used — the
 * padding that keeps the text out from under the glyph would vanish exactly when the text
 * becomes visible. The class is stamped once and survives the swap.
 *
 * ⚠ Sweeping is idempotent via `DONE_ATTR`: a PropertyGrid rebuild re-runs the sweep over
 * fields that already carry a button, and without the mark each rebuild would add another eye.
 */

const DONE_ATTR = "data-m-pwd-reveal";
const PWD_CLASS = "m-pwd-input";

const SHOW_LABEL = "Show password";
const HIDE_LABEL = "Hide password";

function setState(input: HTMLInputElement, btn: HTMLButtonElement, shown: boolean) {
    input.type = shown ? "text" : "password";
    btn.setAttribute("aria-pressed", String(shown));
    btn.setAttribute("aria-label", shown ? HIDE_LABEL : SHOW_LABEL);
    btn.setAttribute("title", shown ? HIDE_LABEL : SHOW_LABEL);
    btn.classList.toggle("is-on", shown);

    const icon = btn.firstElementChild;
    if (icon) icon.className = shown ? "fa fa-eye-slash" : "fa fa-eye";
}

function restoreCaret(input: HTMLInputElement, start: number | null, end: number | null) {
    if (start == null || end == null)
        return;
    // `setSelectionRange` throws on an input type that does not support a selection. Nothing
    // this runs on is one, but the caret is a convenience and must never break the toggle.
    try { input.setSelectionRange(start, end); } catch { /* not selectable */ }
}

function decorate(input: HTMLInputElement) {
    if (input.hasAttribute(DONE_ATTR))
        return;
    input.setAttribute(DONE_ATTR, "1");
    input.classList.add(PWD_CLASS);

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "m-pwd-reveal";
    const icon = document.createElement("i");
    icon.className = "fa fa-eye";
    icon.setAttribute("aria-hidden", "true");
    btn.appendChild(icon);
    setState(input, btn, false);

    // ⚠ Clicking a button steals focus from the input, and on a password field that is
    // noticeable: the caret disappears and typing stops. Suppressing mousedown's default keeps
    // the caret where it was, so the eye can be used mid-word.
    btn.addEventListener("mousedown", e => e.preventDefault());

    btn.addEventListener("click", () => {
        // Changing `type` resets the selection, so the caret is read before the swap and put
        // back after it.
        const start = input.selectionStart, end = input.selectionEnd;
        setState(input, btn, input.type === "password");
        input.focus();
        restoreCaret(input, start, end);

        // ⚠⚠ THE SYNCHRONOUS RESTORE ABOVE IS NOT ENOUGH, and this second one is the whole fix.
        //
        // Chrome finishes a real pointer gesture AFTER the click handler returns, and the tail
        // of that gesture rewrites the caret — so the correct position set above survives the
        // handler, the microtask queue, and then goes to 0 on the next task. Symptom: reveal a
        // password with the caret at the end of what you typed and the caret jumps to the FRONT
        // of the text, so the next keystroke lands at the start.
        //
        // ⚠ THIS IS WHY A SCRIPTED CHECK PASSES WHILE THE REAL BUTTON FAILS. Measured on
        // 2026-09-07: a programmatic `btn.click()` holds the caret at 12 of 12 forever, and a
        // genuine mouse click on the same button reads 12 synchronously, 12 in a microtask and
        // 0 in a `setTimeout(0)`. Verifying this feature by calling `.click()` from the console
        // cannot see the bug — it has to be clicked.
        //
        // ⚠ `setTimeout`, NOT `requestAnimationFrame`: a background or hidden tab does not
        // paint, so its rAF callbacks never run and the caret would stay wrong there.
        setTimeout(() => {
            if (document.activeElement === input)
                restoreCaret(input, start, end);
        }, 0);
    });

    input.insertAdjacentElement("afterend", btn);
}

function sweep() {
    document.querySelectorAll<HTMLInputElement>("input[type='password']:not([" + DONE_ATTR + "])")
        .forEach(decorate);
}

let pending: any;
function sweepSoon() {
    clearTimeout(pending);
    pending = setTimeout(sweep, 30);
}

/**
 * ⚠ THE OBSERVER HAS TO BE CHEAP, because it watches the WHOLE document subtree and the
 * busiest thing in this application is a virtualised grid: SleekGrid adds and removes row
 * nodes continuously while scrolling, and every one of those is a mutation record here. So a
 * record only counts if what it added is, or contains, a password input — a grid row answers
 * that in one `querySelector` over a dozen cells and schedules nothing. Without the test, a
 * scroll re-queried the entire document every 30ms for fields that cannot appear in a row.
 */
function addedAPasswordField(records: MutationRecord[]): boolean {
    for (const r of records) {
        for (const node of r.addedNodes as any as Iterable<Node>) {
            const el = node as Element;
            if (el.nodeType !== 1)
                continue;
            if (el.matches?.("input[type='password']") || el.querySelector?.("input[type='password']"))
                return true;
        }
    }
    return false;
}

export function installPasswordReveal() {
    sweep();

    // Dialogs and property grids are built long after load, so the sweep has to repeat — and
    // the mutations arrive in bursts as a grid renders its fields, so they are coalesced
    // rather than querying the document once per added node.
    new MutationObserver(records => {
        if (addedAPasswordField(records))
            sweepSoon();
    }).observe(document.body, { childList: true, subtree: true });
}
